<template>
  <div class="scms-tracker">
    <div class="page-header">
      <h1>SCMS Tracker</h1>
      <p class="page-description">Visit analytics by month</p>
    </div>

    <div class="month-controls">
      <label for="year-input">Year</label>
      <select
        id="year-input"
        v-model.number="selectedYear"
        class="year-input"
      >
        <option v-for="year in availableYears" :key="year" :value="year">
          {{ year }}
        </option>
      </select>
      <button @click="fetchMonthlyData" class="fetch-btn" :disabled="loading">
        <i class="fa-solid fa-spinner fa-spin" v-if="loading"></i>
        <i class="fa-solid fa-sync" v-else></i>
        {{ loading ? 'Loading...' : 'Load Monthly Data' }}
      </button>
    </div>

    <!-- Results Section -->
    <div class="results-section" v-if="monthlyRows.length || hasSearched">
      <div class="results-header">
        <h3>SCMS monthly analytics</h3>
        <div class="results-info">
          <span class="date-range">Year: {{ selectedYear }}</span>
        </div>
      </div>

      <div v-if="monthlyRows.length" class="table-container">
        <table class="scms-table">
          <thead>
            <tr>
              <th>Month</th>
              <th v-for="column in tableColumns" :key="column.key">{{ column.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in monthlyRows" :key="row.monthKey">
              <td>{{ row.monthLabel }}</td>
              <td v-for="column in tableColumns" :key="`${row.monthKey}-${column.key}`">
                {{ formatMetric(row[column.key]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- No Data Message -->
      <div v-if="!monthlyRows.length && hasSearched && !loading" class="no-data">
        <i class="fa-solid fa-inbox"></i>
        <p>No analytics returned for this year</p>
        <p class="no-data-subtitle">Check SCMS availability</p>
      </div>
    </div>

    <!-- Error Message -->
    <div v-if="error" class="error-message">
      <i class="fa-solid fa-exclamation-triangle"></i>
      <p>{{ error }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from 'axios'

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
})

// Add auth token to requests
api.interceptors.request.use(config => {
  const token = sessionStorage.getItem('token')
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

const tableColumns = [
  { key: 'total_patient_overall', label: 'Total patient overall' },
  { key: 'total_visit', label: 'Total visits' },
  { key: 'total_patient_selangkah_user', label: 'Total patient Selangkah user' },
  { key: 'total_selangkah_user_registered_in_scms', label: 'Selangkah users in SCMS' },
  { key: 'total_new_user', label: 'Total new users' },
  { key: 'total_patient_under_18', label: 'Patients < 18' },
  { key: 'total_patient_above_60', label: 'Patients > 60' },
  { key: 'total_patient_foreigner', label: 'Foreigner patients' },
  { key: 'total_patient_with_mobile_no', label: 'Patients with mobile no.' },
  { key: 'total_patient_with_selangkah_no', label: 'Patients with Selangkah no.' },
  { key: 'existing_selangkah_user', label: 'Existing Selangkah users' },
  { key: 'selangkah_user_visit_gt_1', label: 'Selangkah users (visit > 1)' },
  { key: 'non_selangkah_user', label: 'Non-Selangkah users' }
]

const selectedYear = ref(new Date().getFullYear())
const currentYear = new Date().getFullYear()
const availableYears = [2026, 2025]
const monthlyRows = ref([])
const loading = ref(false)
const error = ref('')
const hasSearched = ref(false)

const formatMetric = (v) => {
  if (v === null || v === undefined || v === '') return '—'
  return typeof v === 'number' ? v.toLocaleString() : String(v)
}

const toYmdLocal = (d) => {
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

onMounted(() => {
  fetchMonthlyData()
})

const getMonthRange = (year, monthIndex) => {
  const start = new Date(year, monthIndex, 1)
  const end = new Date(year, monthIndex + 1, 0)
  const today = new Date()
  const safeEnd = end > today ? today : end
  return {
    start: toYmdLocal(start),
    end: toYmdLocal(safeEnd),
    monthKey: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
    monthLabel: start.toLocaleDateString('en-US', { month: 'short' })
  }
}

const fetchMonthData = async (year, monthIndex) => {
  const month = getMonthRange(year, monthIndex)
  const response = await api.get('/api/finance/scms-tracker', {
    params: {
      start_date: month.start,
      end_date: month.end
    }
  })
  const payload = response.data
  const data = payload?.analytics || payload?.data || payload?.result?.data || {}
  return {
    monthKey: month.monthKey,
    monthLabel: month.monthLabel,
    ...data
  }
}

const fetchMonthlyData = async () => {
  if (!selectedYear.value || selectedYear.value < 2000 || selectedYear.value > currentYear) {
    error.value = 'Please select a valid year'
    return
  }

  loading.value = true
  error.value = ''
  hasSearched.value = true
  monthlyRows.value = []

  try {
    const monthsToLoad = selectedYear.value === currentYear ? new Date().getMonth() + 1 : 12
    const requests = Array.from({ length: monthsToLoad }, (_, i) => fetchMonthData(selectedYear.value, i))
    const rows = await Promise.all(requests)
    monthlyRows.value = rows
  } catch (err) {
    error.value = err.response?.data?.error || 'Failed to fetch SCMS data'
    monthlyRows.value = []
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.scms-tracker {
  padding: 20px;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  margin-bottom: 30px;
}

.page-header h1 {
  color: #1e40af;
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 8px;
}

.page-description {
  color: #6b7280;
  font-size: 1rem;
}

.month-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 24px;
}

.month-controls label {
  color: #374151;
  font-weight: 500;
  font-size: 0.875rem;
}

.year-input {
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #374151;
  background: white;
  transition: all 0.2s ease;
  min-width: 120px;
}

.year-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  color: #0369a1;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 16px;
}

.loading-indicator i {
  font-size: 1rem;
}

.fetch-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  height: fit-content;
}

.fetch-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.fetch-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
}

.results-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  overflow: hidden;
}

.results-header {
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.results-header h3 {
  color: #374151;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 16px;
}

.results-info {
  display: flex;
  gap: 20px;
  color: #6b7280;
  font-size: 0.875rem;
  flex-wrap: wrap;
}

.table-container {
  overflow-x: hidden;
}

.scms-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.scms-table th {
  background: #f3f4f6;
  color: #374151;
  font-weight: 600;
  padding: 10px 8px;
  text-align: center;
  border-bottom: 1px solid #e5e7eb;
  font-size: 0.8rem;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
  position: relative;
}

.sortable-header:hover {
  background: #e5e7eb;
}

.sortable-header i {
  margin-left: 8px;
  font-size: 0.75rem;
  color: #6b7280;
}

.scms-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
  font-size: 0.8rem;
  text-align: center;
  word-break: break-word;
}

.scms-table tbody tr:hover {
  background: #f9fafb;
}

.scms-table tbody tr:last-child td {
  border-bottom: none;
}

.no-data {
  padding: 40px;
  text-align: center;
  color: #6b7280;
}

.no-data i {
  font-size: 3rem;
  margin-bottom: 16px;
  color: #d1d5db;
}

.no-data p {
  font-size: 1rem;
  margin: 0;
}

.no-data-subtitle {
  font-size: 0.875rem !important;
  color: #9ca3af !important;
  margin-top: 8px !important;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.error-message i {
  font-size: 1.25rem;
}

.error-message p {
  margin: 0;
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 768px) {
  .scms-tracker {
    padding: 16px;
  }

  .month-controls {
    flex-direction: column;
    align-items: stretch;
  }

  .fetch-btn {
    width: 100%;
    justify-content: center;
  }

  .results-info {
    flex-direction: column;
    gap: 8px;
  }

  .scms-table {
    font-size: 0.8rem;
  }

  .scms-table th,
  .scms-table td {
    padding: 8px 12px;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.5rem;
  }

  .filter-card {
    padding: 16px;
  }

  .results-header {
    padding: 16px;
  }
}
</style>
